import { useEffect, useState } from 'react'
import { Link, useHistory, useParams } from 'react-router-dom'
import ArticleIconTags from '../../../components/ArticleIconTags/ArticleIconTags'
import Blockquote from '../../../components/Blockquote/Blockquote'
import Embed from '../../../components/Embed/Embed'
import Details from '../../../components/Details/Details'
import Image from '../../../components/Image/Image'
import RichText from '../../../components/RichText/RichText'
import Swiper from '../../../components/Swiper/Swiper'

import { getCssVar } from '../../../utils/css'
import { getInitials } from '../../../utils/strings'
import { graphqlClient, gql } from '../../../utils/gql'
import { setDocumentTitle } from '../../../utils/dom'

export default function DashboardContent (props = {}) {
    const history = useHistory()

    const params = useParams()

    const cachedArticle = (history.location.state || {}).article

    const [article, setArticle] = useState(cachedArticle
            && (cachedArticle.id === params.slug
                || cachedArticle.slug === params.slug)
        ? cachedArticle
        : null)

    const [relatedArticles, setRelatedArticles] = useState(
        (history.location.state || {}).relatedArticles
            || null)

    useEffect(async () => {
        const isSlug = !(params.slug || '').match(/^\d+$/)

        const queryKey = isSlug
            ? 'slug'
            : 'id'

        const queryValue = isSlug
            ? `"${params.slug}"`
            : params.slug

        const queryArticle = await graphqlClient
            .request(gql`
                query {
                    articles (
                        limit: 1,
                        where: {
                            ${queryKey}: {
                                _eq: ${queryValue}
                            }
                        }
                    ) {
                        id
                        title
                        slug
                        created_at
                        updated_at
                        durations
                        author
                        content
                        resources_text
                        resources
                        thumbnail
                        tags {
                            id
                            slug
                            title
                        },
                        source {
                            id
                            title
                            slug
                        }
                        source_attribution_url
                    }
                }`)
            .then(obj => (obj.articles || [])[0])
            .catch((err) => {
                console.warn(err)
                return false
            })

        if (!article
                || JSON.stringify(article)
                    !== JSON.stringify(queryArticle)) {
            setArticle(queryArticle || false)

            history.replace(
                `${history.location.pathname}${history.location.search}`,
                Object.assign(history.location.state || {}, {
                    article: queryArticle
                }))
        }

        if (!queryArticle
                || (relatedArticles && relatedArticles.length > 2))
            return

        const relatedQueryWhereId = gql`${queryKey}: { _neq: ${queryValue} }`

        const sourceId = (queryArticle.source || {}).id || 0

        const relatedQueryWhereSourceId = sourceId
            ? gql`source_id: { _eq: ${sourceId} }`
            : gql`source_id: { _is_null: true }`

        const relatedQueryWhere
            = gql`${relatedQueryWhereId}, ${relatedQueryWhereSourceId}`

        const getRelatedQuery = async (where = '') => await graphqlClient
            .request(gql`
                query {
                    articles (
                        where: {
                            status: { _eq: "live" },
                            ${where}
                        },
                        limit: 24,
                        order_by: {
                            created_at: desc
                        }
                    ) {
                        id
                        title
                        slug
                        created_at
                        durations
                        thumbnail
                        tags {
                            id
                            slug
                            title
                        }
                        source {
                            id
                            slug
                            title
                        }
                    }
                }`)
            .catch((err) => {
                console.warn(err)
                return ({ articles: [] })
            })

        const relatedQuery = await getRelatedQuery(relatedQueryWhere)

        if (!relatedQuery.articles.length < 3) {
            const relatedQueryFallback = await getRelatedQuery()

            const relatedQueryIds = relatedQuery.articles
                .map(obj => obj.id)

            relatedQuery.articles = relatedQuery.articles
                .concat(relatedQueryFallback.articles
                    .filter(obj => relatedQueryIds
                        .indexOf(obj.id) < 0))
        }

        const tagIds = queryArticle.tags
            .map(obj => obj.id)

        let relatedQueryArticles = relatedQuery.articles
            .filter(obj => obj.id !== queryArticle.id)
            .sort((a, b) => {
                const countA = (a.tags || [])
                    .filter(obj => tagIds.indexOf(obj.id) >= 0)
                    .length

                const countB = (b.tags || [])
                    .filter(obj => tagIds.indexOf(obj.id) >= 0)
                    .length

                if (countA === countB)
                    return 0

                return countA < countB
                    ? -1
                    : 1
            })

        if (sourceId)
            relatedQueryArticles = relatedQueryArticles
                .sort((a, b) => {
                    const sourceA = (a.source || {}).id || 0

                    const sourceB = (b.source || {}).id || 0

                    const isSourceAMatch = sourceA
                        && sourceA === sourceId

                    const isSourceBMatch = sourceB
                        && sourceB === sourceId

                    if (isSourceAMatch && isSourceBMatch)
                        return 0

                    return isSourceAMatch
                        ? -1
                        : 1
                })

        if (!relatedQueryArticles)
            console.warn(relatedQuery)

        setRelatedArticles(relatedQueryArticles.slice(0, 3))

        history.replace(
            `${history.location.pathname}${history.location.search}`,
            Object.assign(history.location.state || {}, {
                relatedArticles: relatedQueryArticles.slice(0, 3)
            }))
    }, [])

    useEffect(() => {
        if (!article)
            return article === false
                && history.replace('/dashboard/hub')

        setDocumentTitle(article.title)
    }, [article])

    if (!article
            || relatedArticles === null)
        return null

    const isResourcesAside = Boolean((article.resources || []).length)
        || Boolean((relatedArticles || []).length)

    const renderImage = (props) => {
        const {
            img,
            caption,
            ...renderProps
        } = props

        if (!(img || {}).src)
            return null

        if (renderProps.type)
            delete renderProps.type

        return (
            <figure
                {...renderProps}
            >
                <Image
                    alt=""
                    src={`${img.src}?w=920&q=71`}
                    loading="lazy"
                    {...img}
                />
                {caption
                    && (
                        <figcaption>
                            {caption}
                        </figcaption>)}
            </figure>
        )
    }

    return (
        <div className="dashboard-content display-flex flex-direction-column flex-grow">
            <div className="pad-3-t pad-4-b-xl-up">
                <div className="container">
                    <div className="row-condensed flex-justify-content-center">
                        <div
                            className={`col-12${!isResourcesAside
                                ? ' col-8-xl-up'
                                : ''}`}
                        >
                            <Link
                                to="/dashboard/hub"
                                className="body-4 display-inline-flex"
                                style={{
                                    color: getCssVar('--slate'),
                                    marginBottom: '25px',
                                    lineHeight: 1.1
                                }}
                            >
                                <svg
                                    className="icon-chevron-left flex-none"
                                    width="18"
                                    height="18"
                                    style={{
                                        color: getCssVar('--oxford'),
                                        margin: '-2px 2px 0 -5px'
                                    }}
                                >
                                    <use xlinkHref="#icon-chevron-left" />
                                </svg>
                                <span>Go back to Content hub</span>
                            </Link>
                        </div>
                    </div>
                    <article className="row-condensed flex-justify-content-center">
                        <div className="col-12 col-8-lg-up">
                            <div className="card card-offscreen-x-lg-down pad-3-t-lg-up pad-5-b-lg-up margin-1-b margin-3-b-lg-up">
                                <div className="dashboard-content-body">
                                    <header className="margin-4-b">
                                        <div className="row-condensed">
                                            <div className="col-12">
                                                {article.title
                                                    && (
                                                        <h1
                                                            className="heading-2 display margin-b"
                                                            aria-describedby="dashboard-content-author dashboard-content-tags"
                                                        >
                                                            {article.title}
                                                        </h1>)}
                                                <ArticleIconTags
                                                    tags={article.tags}
                                                    durations={article.durations}
                                                    id="dashboard-content-tags"
                                                    className="display-flex flex-wrap body-4"
                                                    containerClassName="display-flex margin-b flex-align-items-center"
                                                    linkTags
                                                />
                                                {Boolean((article.author || {}).name
                                                        || (article.source || {}).title)
                                                    && (
                                                        <div className="row-condensed body-4">
                                                            {(article.author || {}).name
                                                                && (
                                                                    <div className="col display-inline-flex flex-nowrap flex-align-items-center">
                                                                        {article.author.img
                                                                            && article.author.img.src
                                                                            && (
                                                                                <div className="avatar-sm margin-1-r">
                                                                                    <Image
                                                                                        alt={`Avatar of ${article.author.name}`}
                                                                                        data-object-fit="cover"
                                                                                        {...article.author.img}
                                                                                        src={`${article.author.img.src}?w=64`}
                                                                                        textFallback={getInitials(article.author.name)}
                                                                                    />
                                                                                </div>)}
                                                                        <p id="dashboard-content-author">
                                                                            <span className="text-screen-reader">by</span>
                                                                            {article.author.name}
                                                                            {article.author.title
                                                                                && ` | ${article.author.title}`}
                                                                            {article.author.company
                                                                                && ` | ${article.author.company}`}
                                                                            <span style={{ opacity: 0 }}>.</span>
                                                                        </p>
                                                                    </div>)}
                                                            {(article.source || {}).title
                                                                && (
                                                                    <div
                                                                        id="dashboard-content-source"
                                                                        className="col-auto col-12-md-down mt-0-md-up"
                                                                        style={{
                                                                            marginTop: (article.author || {}).name
                                                                                ? 3
                                                                                : null
                                                                        }}
                                                                    >
                                                                        <p>
                                                                            <span
                                                                                style={{
                                                                                    color: getCssVar('--cadet')
                                                                                }}
                                                                            >
                                                                                Source&nbsp;|&nbsp;
                                                                            </span>
                                                                            <Link
                                                                                to={`/dashboard/hub/search?source=${
                                                                                    article.source.slug}`}
                                                                            >
                                                                                {article.source.title}
                                                                            </Link>
                                                                        </p>
                                                                    </div>
                                                                )}
                                                        </div>)}
                                            </div>
                                        </div>
                                    </header>
                                    {(article.content || [])
                                        .map((obj, idx) => {
                                            if (obj.type === 'RichText')
                                                return (
                                                    <RichText
                                                        key={`dashboard-content-flex-${idx}`}
                                                        className="margin-b"
                                                        html={obj.richText}
                                                    />
                                                )

                                            if (obj.type === 'Blockquote')
                                                return (
                                                    <Blockquote
                                                        key={`dashboard-content-flex-${idx}`}
                                                        {...obj}
                                                    />
                                                )

                                            if (obj.type === 'Accordions'
                                                    && (obj.items || []).length)
                                                return obj.items
                                                    .map((_obj, _idx) => (
                                                        <Details
                                                            key={`dashboard-content-flex-${idx}-${_idx}`}
                                                            className="card-border margin-b"
                                                            {..._obj}
                                                        />
                                                    ))

                                            if (obj.type === 'Embed'
                                                    && obj.embedUrl)
                                                return (
                                                    <Embed
                                                        key={`dashboard-content-flex-${idx}`}
                                                        oembed={obj.embedUrl}
                                                        color={obj.color}
                                                        className={!idx
                                                            ? 'margin-4-b margin-5-b-lg-up'
                                                            : 'margin-b'}
                                                        soundcloudVisualPlayer={
                                                            !article.source_attribution_url
                                                                || (article.source_attribution_url
                                                                    !== obj.embedUrl)
                                                        }
                                                    />
                                                )

                                            if ((obj.type === 'Image'
                                                        && obj.img
                                                        && obj.img.src)
                                                    || (obj.type === 'Slideshow'
                                                        && (obj.items || []).length
                                                            === 1))
                                                return renderImage(Object.assign({
                                                    key: `dashboard-content-flex-${idx}`
                                                }, obj.items
                                                    ? obj.items[0] || {}
                                                    : obj))

                                            if (obj.type === 'Slideshow'
                                                    && (obj.items || []).length > 1)
                                                return (
                                                    <div
                                                        className="dashboard-content-swiper display-flex flex-direction-column"
                                                        key={`dashboard-content-flex-${idx}`}
                                                    >
                                                        <h2
                                                            id={`dashboard-content-slider-${idx}-heading`}
                                                            className="text-screen-reader"
                                                        >
                                                            Image slideshow
                                                        </h2>
                                                        <Swiper
                                                            slideClassName="dashboard-content-swiper-item"
                                                            slidesPerView={1}
                                                            pagination={{
                                                                type: 'fraction'
                                                            }}
                                                            autoHeight
                                                            effect="fade"
                                                        >
                                                            {obj.items
                                                                .map((_obj, _idx) => (
                                                                    <div
                                                                        key={`dashboard-content-slider-${idx}-${_idx}`}
                                                                        className="display-flex"
                                                                    >
                                                                        {renderImage(Object.assign(_obj, {
                                                                            loading: null
                                                                        }))}
                                                                    </div>
                                                                ))}
                                                        </Swiper>
                                                    </div>
                                                )

                                            if (obj.type === 'TextImageColumns'
                                                    && (obj.items || []).length)
                                                return (
                                                    <div
                                                        className="dashboard-content-text-image-columns"
                                                        key={`dashboard-content-flex-${idx}`}
                                                    >
                                                        <Swiper
                                                            wrapperClassName="row-condensed"
                                                            slideClassName="dashboard-content-text-image-columns-item col-12 col-7-md col-6-lg-up"
                                                            navigation={false}
                                                            pagination={false}
                                                        >
                                                            {obj.items
                                                                .filter(_obj => _obj.title
                                                                    && _obj.text)
                                                                .map((_obj, _idx) => (
                                                                    <div
                                                                        key={`dashboard-content-flex-${idx}-${_idx}`}
                                                                        className="display-flex flex-direction-column"
                                                                    >
                                                                        {_obj.img
                                                                            && _obj.img.src
                                                                            && (
                                                                                <figure className="margin-1-b">
                                                                                    <div className="ratio-wide">
                                                                                        <Image
                                                                                            className="position-absolute"
                                                                                            data-object-fit="cover"
                                                                                            {..._obj.img}
                                                                                            src={`${_obj.img.src}?w=920&q=71`}
                                                                                            loading="lazy"
                                                                                        />
                                                                                    </div>
                                                                                    {_obj.caption
                                                                                        && (
                                                                                            <figcaption>
                                                                                                {_obj.caption}
                                                                                            </figcaption>)}
                                                                                </figure>)}
                                                                        <h4
                                                                            className="typography-no-breakpoints"
                                                                        >
                                                                            {_obj.title}
                                                                        </h4>
                                                                        <RichText
                                                                            html={_obj.text}
                                                                        />
                                                                    </div>))}
                                                        </Swiper>
                                                    </div>
                                                )

                                            return null
                                        })}

                                </div>
                            </div>
                        </div>
                        {isResourcesAside
                            && (
                                <aside className="dashboard-content-aside col-12 col-4-lg-up">
                                    {Boolean((article.resources || []).length)
                                        && (
                                            <section className="card card-offscreen-x-lg-down margin-1-b margin-3-b-lg-up">
                                                <h2 className="heading-6 margin-1-b font-weight-medium">Related resources</h2>
                                                <div className="body-4 dashboard-content-aside-body">
                                                    {article.resources_text
                                                        && (
                                                            <p className="margin-b">
                                                                {article.resources_text}
                                                            </p>)}
                                                    <ul>
                                                        {article.resources
                                                            .filter(obj => obj.url && obj.title)
                                                            .map(obj => (Object.assign(obj, {
                                                                icon: (obj.url || '').match(/\.pdf$/)
                                                                    ? 'icon-picture-as-pdf'
                                                                    : 'icon-launch'
                                                            })))
                                                            .map((obj, idx, arr) => (
                                                                <li
                                                                    key={`dashboard=content-aside-${idx}`}
                                                                    style={{
                                                                        marginBottom: idx + 1
                                                                                !== arr.length
                                                                            ? 5
                                                                            : 0
                                                                    }}
                                                                >
                                                                    <a
                                                                        href={obj.url}
                                                                        className="display-inline-flex text-decoration-hover flex-align-items-start flex-nowrap"
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        title="Opens in new tab"
                                                                    >
                                                                        <svg
                                                                            className={`${obj.icon} flex-none margin-1-r`}
                                                                            width="18"
                                                                            height="18"
                                                                        >
                                                                            <use xlinkHref={`#${obj.icon}`} />
                                                                        </svg>
                                                                        <span className="font-weight-medium">
                                                                            {obj.title}
                                                                        </span>
                                                                    </a>
                                                                </li>
                                                            ))}
                                                    </ul>
                                                </div>
                                            </section>)}
                                    {Boolean((relatedArticles || []).length)
                                        && (
                                            <section className="card card-offscreen-x-lg-down margin-1-b margin-3-b-lg-up">
                                                <div className="dashboard-content-related">
                                                    <h2 className="heading-6 font-weight-medium margin-b">Related content</h2>
                                                    <ul className="row-condensed">
                                                        {relatedArticles
                                                            .map((obj, idx) => (
                                                                <li
                                                                    className="dashboard-content-related-item col-12 display-flex"
                                                                    key={`dashboard-home-article-${idx}}`}
                                                                >
                                                                    <Link
                                                                        className="display-flex text-decoration-none flex-grow flex-nowrap flex-align-items-start"
                                                                        to={`/dashboard/hub/${obj.slug || obj.id}`}
                                                                    >
                                                                        <div className="col-auto pad-0-l pad-2-r">
                                                                            <div
                                                                                className="ratio-square pad-0"
                                                                                style={{ width: 64 }}
                                                                            >
                                                                                <Image
                                                                                    src={obj.thumbnail
                                                                                            && (obj.thumbnail || '').match(/\//)
                                                                                        ? obj.thumbnail
                                                                                        : '/images/dashboard-content-thumbnail-fallback.svg'}
                                                                                    alt=""
                                                                                    data-object-fit="cover"
                                                                                    loading="lazy"
                                                                                />
                                                                            </div>
                                                                        </div>
                                                                        <div
                                                                            className="col pad-0 display-flex flex-direction-column flex-justify-content-center"
                                                                            style={{ minHeight: '100%' }}
                                                                        >
                                                                            <h3 className="dashboard-content-related-item-title body-4 font-weight-medium">
                                                                                {obj.title}
                                                                            </h3>
                                                                            <ArticleIconTags
                                                                                className="margin-1-t display-flex flex-align-items-center flex-nowrap body-6 color-slate"
                                                                                durations={obj.durations}
                                                                                tags={obj.tags}
                                                                                hideTags
                                                                            />
                                                                        </div>
                                                                    </Link>
                                                                </li>
                                                            ))}
                                                    </ul>
                                                </div>
                                            </section>)}
                                </aside>)}
                    </article>
                </div>
            </div>
        </div>
    )
}
